import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Product from "./Card";
import { useCart } from "../../context/CartContext";
import productAPI from "../../api/productApi";
import Breadcrump from "../../components/Breadcrump";
import SEO from "../../components/SEO";
import KeyFeatures from "./detailfeatures/KeyFeatures";
import ProductCard from "./Card";
import { Col, Row } from "react-bootstrap";
import { Check, Heart, ImageIcon, MessageSquareText, X } from "lucide-react";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";
import { showError } from "../../utils/toastUtils";
import DynamicField from "../customizeform/DynamicFirelds";
import FindMyModalSize from "../FindMyModalSize";

function DetailPage() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);

  const thumbRef = useRef();

  const [product, setProduct] = useState(null);
  const [detail, setDetail] = useState(null);
  const [variants, setVariants] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [displayedImages, setDisplayedImages] = useState([]);

  const [mainImage, setMainImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const [showScrollIcon, setShowScrollIcon] = useState(false);

  const [viewFixVideo, setViewFixVideo] = useState(false);

  const [title, setTitle] = useState();
  const [brand, setBrand] = useState({});
  const [skuCode, setSkuCode] = useState("");
  const [category, setCategory] = useState({});
  const [description, setDescription] = useState("");
  const [stock, setStock] = useState("");
  const [formData, setFormData] = useState({});
  const [isCustomizationDone, setIsCustomizationDone] = useState(false);

  const [selectedSize, setSelectedSize] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [showSizeChart, setShowSizeChart] = useState(false);

  /******************* FETCH PRODUCT *******************/
  const fetchProductDetail = async () => {
    try {
      const res = await productAPI.details(slug);

      if (res.code === 200) {
        const {
          product,
          detail,
          brand,
          category,
          variants,
          relatedProducts,
          isWishlisted,
        } = res.data;

        setProduct(product);

        setIsWishlisted(isWishlisted || false);

        setDetail(detail);
        setBrand(brand);
        setCategory(category);
        setVariants(variants || []);
        setRelatedProducts(relatedProducts || []);

        setTitle(product?.title);
        setSkuCode(product?.skuCode);
        setDescription(detail?.description);

        // default variant
        if (variants?.length > 0) {
          setSelectedVariant(variants[0]);

          if (product?.productType === "Variant") {
            setSelectedAttributes({
              Fabric: variants[0]?.attributes?.Fabric || "",
              Colour: variants[0]?.attributes?.Colour || "",
            });
          }
        }

        // images
        const images = [];
        if (product?.thumbnail) {
          images.push(product.thumbnail);
        }

        if (detail?.images?.length) {
          images.push(...detail.images);
        }

        if (images.length > 0) {
          setMainImage(images[0]);
          setDisplayedImages(images);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProductDetail();
  }, [slug]);

  /******************* IMAGES *******************/
  const mergedImages = [
    ...(product?.thumbnail ? [product.thumbnail] : []),
    ...(detail?.images || []),
  ];

  /******************* VARIANT ATTRIBUTES *******************/
  const attributeMap = {};

  variants.forEach((variant) => {
    Object.entries(variant.attributes || {}).forEach(([key, value]) => {
      if (!attributeMap[key]) {
        attributeMap[key] = new Set();
      }

      attributeMap[key].add(value);
    });
  });

  const handleVariantClick = (key, value) => {
    const updated = {
      ...selectedAttributes,
      [key]: value,
    };

    setSelectedAttributes(updated);

    const matched = variants.find((variant) =>
      Object.entries(updated).every(([k, v]) => variant.attributes?.[k] === v),
    );

    if (matched) {
      setSelectedVariant(matched);
    }
  };

  // Create attribute map
  if (variants) {
    variants.forEach((variant) => {
      if (variant.attributes && typeof variant.attributes === "object") {
        Object.entries(variant.attributes).forEach(([key, value]) => {
          if (!attributeMap[key]) {
            attributeMap[key] = new Set();
          }
          attributeMap[key].add(value);
        });
      }
    });
  }

  // Set default attributes on mount
  useEffect(() => {
    const defaultSelected = {};
    Object.entries(attributeMap).forEach(([key, valueSet]) => {
      const values = Array.from(valueSet);
      if (values.length > 0) {
        defaultSelected[key] = values[0];
      }
    });

    setSelectedAttributes(defaultSelected);
  }, []);

  const extractYouTubeID = (url) => {
    const regExp = /(?:youtube\.com.*(?:\?|&)v=|youtu\.be\/)([^&?/]+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // to fix bottom
  useEffect(() => {
    const handleScroll = () => {
      const quantityEl = document.querySelector(".action-btn-detail");
      const stickyEl = document.querySelector(".tf-sticky-btn-atc");
      const floatingVideo = document.querySelector(".floating-animate");

      if (!quantityEl || !stickyEl || !floatingVideo) return;

      const quantityBottom = quantityEl.getBoundingClientRect().bottom;
      const windowHeight = window.innerHeight;

      if (quantityBottom <= windowHeight) {
        stickyEl.classList.remove("show");
        floatingVideo.classList.add("active");
      } else {
        stickyEl.classList.add("show");
        floatingVideo.classList.remove("active");
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const checkScroll = () => {
      const el = thumbRef.current;
      if (el && el.scrollHeight > el.clientHeight) {
        setShowScrollIcon(true);
      } else {
        setShowScrollIcon(false);
      }
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);

    return () => window.removeEventListener("resize", checkScroll);
  }, [mergedImages]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preSelectedAttributes = {};

    for (const [key, value] of params.entries()) {
      preSelectedAttributes[key] = value;
    }

    if (Object.keys(preSelectedAttributes).length > 0 && variants.length > 0) {
      setSelectedAttributes(preSelectedAttributes);

      const matched = variants.find((variant) =>
        Object.entries(preSelectedAttributes).every(
          ([key, val]) => variant.attributes?.[key] === val,
        ),
      );

      if (matched) {
        setSelectedVariant(matched);
        setTitle(`${product?.title} - ${matched.variant_name}`);
        setSkuCode(matched.sku || product?.skuCode);
        setDescription(matched.description || detail?.description);

        const variantDetailImages = matched.images
          ? matched.images.map((img) => img)
          : [];

        const mergedImages = [...variantDetailImages];

        if (mergedImages.length > 0) {
          setMainImage(mergedImages[0]);
        }
      }
    }
  }, [variants]);

  /******************* CART *******************/
  const handleAddToCart = async (e) => {
    e.preventDefault();

    const action = e.currentTarget.dataset.action;

    // Sirf unhi products ke liye check karo jinke custom fields hain
    if (product?.customFields?.length > 0 && !isCustomizationDone) {
      showError("Please complete the customization form first.");
      return;
    }

    if (product?.customFields?.length > 0) {
      const requiredFields = product.customFields.filter(
        (field) => field.required,
      );

      const missingFields = requiredFields.filter((field) => {
        const value = formData[field.key];

        if (field.type === "image") {
          return !value;
        }

        return !value || value.toString().trim() === "";
      });

      if (missingFields.length > 0) {
        showError("Please complete the customization form first.");
        return;
      }
    }

    try {
      await addToCart(product._id, quantity, selectedVariant?._id, formData);

      if (action === "cart") {
        const canvas = document.getElementById("shoppingCartCanvas");

        const offcanvas = new window.bootstrap.Offcanvas(canvas);
        offcanvas.show();
      }

      if (action === "buy-now") {
        navigate("/checkout");
      }
    } catch (err) {
      console.error(err);
    }
  };

  /******************* THUMB SCROLL *******************/

  useEffect(() => {
    const checkScroll = () => {
      const el = thumbRef.current;

      if (!el) return;

      if (el.scrollHeight > el.clientHeight) {
        setShowScrollIcon(true);
      } else {
        setShowScrollIcon(false);
      }
    };

    checkScroll();

    window.addEventListener("resize", checkScroll);

    return () => window.removeEventListener("resize", checkScroll);
  }, [mergedImages]);

  /******************* LOADING *******************/
  if (!product) return <div className="container mt-5">Loading...</div>;

  /************* FUNCTIONS FOR DEFINE*****************/
  const price = selectedVariant
    ? selectedVariant.price
    : product?.productType === "Variant"
      ? detail?.pricing?.minPrice
      : detail?.pricing?.minPrice;

  const inStock = selectedVariant ? Number(selectedVariant.stock) > 0 : true;

  const handleWishlistToggle = async (e) => {
    e.preventDefault();

    const isLoggedIn = Auth.getToken();

    if (!isLoggedIn) {
      showError("Please log in to manage your wishlist");
      return;
    }

    try {
      const res = await wishlistAPI.addProduct({ productId: product?._id });
      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }
    } catch (error) {
      showError("Failed to update wishlist");
    }
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const hasCustomization = product?.customFields?.length > 0;

  const isCustomizationComplete =
    !hasCustomization ||
    product.customFields
      .filter((field) => field.required)
      .every((field) => {
        const value = formData[field.key];
        return value && value.toString().trim() !== "";
      });

  return (
    <>
      <Breadcrump title={product?.title} />
      <SEO meta={detail?.meta} />

      <section className="detail-page mt-5">
        <div className="tf-main-product section-image-zoom">
          <div className="container">
            <div className="row  gx-md-4 align-items-start">
              <div className="col-md-7">
                <div className="custom-left-sticky">
                  <div className="editorial-gallery ">
                    <Row className="g-3">
                      {displayedImages.slice(0, 4).map((img, index) => (
                        <Col xs={6} key={index}>
                          <a
                            href={img}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="secondary-img"
                          >
                            <img
                              src={img}
                              alt={`Detail-${index}`}
                              className="img-fluid"
                              onClick={() => setMainImage(img)}
                            />
                          </a>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </div>
              </div>

              <div className="col-md-5">
                <div className="tf-product-heading">
                  {brand && (
                    <span>
                      <Link
                        to={`/brands/${brand?.slug}`}
                        className="text-decoration-none brand-tag"
                      >
                        {brand?.name}
                      </Link>
                    </span>
                  )}

                  <h5 className="product-display-title">{title}</h5>

                  {/* variant wise amount showing */}
                  <div>
                    {selectedVariant ? (
                      <>
                        <div className="product-price">
                          ₹{selectedVariant?.price?.toFixed(2)}
                        </div>
                        {selectedVariant?.mrp &&
                          selectedVariant?.mrp > selectedVariant.price && (
                            <>
                              <div className="price-tag mb-2  ">
                                ₹{selectedVariant?.mrp?.toFixed(2)}
                              </div>
                              <span className="badge-sale">
                                (
                                {Math.round(
                                  ((selectedVariant.mrp -
                                    selectedVariant.price) /
                                    selectedVariant.mrp) *
                                    100,
                                )}
                                % Off)
                              </span>
                            </>
                          )}
                      </>
                    ) : (
                      <div className="display-sm price-new">₹{price}</div>
                    )}
                  </div>
                  {/* variant wise amount section end */}

                  {(product.skuCode || category) && (
                    <div className="item">
                      <ul>
                        {product.skuCode && (
                          <li>
                            <b>SKU:</b> {product.skuCode}
                          </li>
                        )}
                        {category && (
                          <li>
                            <b>Category:</b>{" "}
                            <Link
                              to={`/categories/${category?.slug}`}
                              className="text-decoration-none text-muted"
                            >
                              {category.name}
                            </Link>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  <hr />

                  {detail?.shortDescription && (
                    <p className="description-text">
                      {detail?.shortDescription}
                    </p>
                  )}

                  <hr />
                </div>

                {product?.productType === "Variant" && variants?.length > 0 && (
                  <div className="custom-variant-wrapper">
                    {/* Fabric */}
                    <div className="custom-fabric-wrapper">
                      <h6 className="variant-heading">Select your Fabric</h6>

                      <div className="fabric-list">
                        {[
                          ...new Set(
                            variants
                              .filter((v) => Number(v.stock) > 0)
                              .map((v) => v.attributes?.Fabric),
                          ),
                        ]
                          .filter(Boolean)
                          .map((fabric) => (
                            <button
                              key={fabric}
                              className={`fabric-btn ${
                                selectedAttributes.Fabric === fabric
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                handleVariantClick("Fabric", fabric)
                              }
                            >
                              {fabric}
                            </button>
                          ))}
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="custom-color-wrapper mt-4">
                      <h6 className="variant-heading">Available Colors</h6>

                      <div className="color-list">
                        {variants
                          .filter(
                            (v) =>
                              v.attributes?.Fabric ===
                                selectedAttributes.Fabric &&
                              Number(v.stock) > 0,
                          )
                          .map((variant) => (
                            <div
                              key={variant._id}
                              className={`color-card ${
                                selectedVariant?._id === variant._id
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => {
                                setSelectedVariant(variant);
                                setSelectedAttributes({
                                  Fabric: variant.attributes?.Fabric || "",
                                  Colour: variant.attributes?.Colour || "",
                                });
                              }}
                            >
                              <img
                                src={variant.images?.[0]}
                                alt={variant.attributes?.Colour}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPreview(variant);
                                  setShowImageModal(true);
                                }}
                              />
                              <span>{variant.attributes?.Colour}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="d-flex gap-3">
                  {product?.customFields?.length > 0 && (
                    <button
                      className="find-size-btn mb-3"
                      onClick={() => {
                        setIsCustomizationDone(false);
                        setShowModal(true);
                      }}
                    >
                      Customize Size
                    </button>
                  )}

                  <FindMyModalSize
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    fields={product.customFields}
                    formData={formData}
                    onChange={handleChange}
                    onSelectSize={(size) => {
                      setSelectedSize(size);
                      setIsCustomizationDone(true);
                      setShowModal(false);
                    }}
                  />


                  <button
                    type="button"
                    className="find-size-chart-btn mb-3"
                    onClick={() => setShowSizeChart(true)}
                  >
                    Size Chart
                  </button>
                </div>

                <div className="tf-product-total-quantity ">
                  <div className="group-btn d-flex gap-2 align-items-center">
                    <div className="custom-qty-wrapper">
                      <button
                        className="custom-qty-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          if (quantity > 1) setQuantity(quantity - 1);
                        }}
                      >
                        -
                      </button>
                      <input
                        className="custom-qty-input"
                        type="text"
                        name="number"
                        value={quantity}
                        readOnly
                      />
                      <button
                        className="custom-qty-btn"
                        onClick={(e) => setQuantity(quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className={`detail-wishlist-btn ${isWishlisted ? "active" : ""}`}
                      onClick={handleWishlistToggle}
                      aria-label="Add to wishlist"
                    >
                      <Heart size={24} className="detail-wishlist-icon me-2" />
                      Wishlist
                    </button>
                  </div>
                  <div className="action-stack mt-4">
                    {/* <a
                      href={
                        isCustomizationComplete ? "#shoppingCartCanvas" : "#"
                      }
                      data-bs-toggle={
                        isCustomizationComplete ? "offcanvas" : ""
                      }
                      className="btn btn-dark btn-detail text-decoration-none w-100 mt-2"
                      onClick={handleAddToCart}
                      data-action="cart"
                    >
                      Add to Cart
                    </a> */}
                    <button
                      type="button"
                      className="btn btn-dark btn-detail w-100 mt-2"
                      onClick={handleAddToCart}
                      data-action="cart"
                    >
                      Add to Cart
                    </button>
                    <a
                      href="#"
                      className=" btn btn-outline-dark btn-detail text-decoration-none w-100 mt-2"
                      onClick={handleAddToCart}
                      data-action="buy-now"
                    >
                      Buy it now
                    </a>
                  </div>
                </div>

                {detail.video && viewFixVideo && (
                  <>
                    {detail.video.includes("youtube.com") ||
                    detail.video.includes("youtu.be") ? (
                      <iframe
                        width="100%"
                        height="450"
                        src={`https://www.youtube.com/embed/${extractYouTubeID(
                          detail.video,
                        )}`}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        title="YouTube video"
                      ></iframe>
                    ) : (
                      <video width="100%" height="450" controls autoPlay muted>
                        <source src={detail.video} type="video/mp4" />
                        Your browser does not support HTML5 video.
                      </video>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pf-wrap">
        <div className="container">
          <h2 className="custom-feature-heading">Product highlights</h2>

          {detail?.keyFeatures?.length > 0 && (
            <div className="pf-card">
              {detail.keyFeatures.map((feature, index) => (
                <div className="pf-row" key={index}>
                  <span className="pf-label">{feature?.label}</span>
                  <span className="pf-value">{feature?.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* <section className="custom-features-section">
        <div className="container">
          <h2 className="custom-features-heading">Product Highlights</h2>

          {detail?.keyFeatures?.length > 0 && (
            <div className="custom-features-list">
              {detail.keyFeatures.map((feature, index) => (
                <div className="custom-feature-item" key={index}>
                  <span className="custom-feature-label">{feature?.label}</span>

                  <span className="custom-feature-value">{feature?.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section> */}
      <section className="custom-desc-section mb-md-5 mb-3">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="custom-desc-heading">Product Description</h2>
              <div
                className="custom-desc-content"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          </div>
        </div>
      </section>

      {relatedProducts && relatedProducts.length > 0 && (
        <div className="related-products-section bg-light py-5">
          <div className="container product">
            <div className="flat-animate-tab flat-controltab-nav">
              <div className="text-start mb-4">
                <h4 className="section-title">Related Products</h4>
              </div>
              <div className="row">
                {relatedProducts.map((product, index) => (
                  <div
                    className="col-lg-3 col-sm-6 col-xs-6 mb-2"
                    key={product?._id || index}
                  >
                    <Product item={product} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showImageModal && selectedPreview && (
        <div
          className="custom-image-preview-overlay"
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="custom-image-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="custom-preview-header">
              <div className="custom-preview-title">
                <i className="fa-solid fa-layer-group"></i>
                <span>Select Fabric</span>
              </div>

              <button
                className="custom-preview-close"
                onClick={() => setShowImageModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            {/* Image */}
            <div className="custom-preview-image">
              <img
                src={selectedPreview.images?.[0]}
                alt={selectedPreview.attributes?.Colour}
              />
            </div>

            {/* Details */}
            <div className="custom-preview-content">
              <div className="custom-preview-info">
                <div className="custom-preview-row">
                  <span>Colour</span>
                  <strong>{selectedPreview.attributes?.Colour}</strong>
                </div>

                <div className="custom-preview-row">
                  <span>Fabric</span>
                  <strong>{selectedPreview.attributes?.Fabric}</strong>
                </div>
              </div>

              <button
                className="custom-select-fabric-btn"
                onClick={() => {
                  setSelectedVariant(selectedPreview);

                  setSelectedAttributes({
                    Fabric: selectedPreview.attributes?.Fabric,
                    Colour: selectedPreview.attributes?.Colour,
                  });

                  setShowImageModal(false);
                }}
              >
                <i className="fa-solid fa-check"></i>
                Select This Fabric
              </button>
            </div>
          </div>
        </div>
      )}

      {/* size chart modal */}
      {showSizeChart && (
        <div
          className="custom-size-chart-overlay"
          onClick={() => setShowSizeChart(false)}
        >
          <div
            className="custom-size-chart-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="custom-size-chart-header">
              <h5>Size Chart</h5>

              <button
                type="button"
                className="custom-size-chart-close"
                onClick={() => setShowSizeChart(false)}
              >
                <X size={22} />
              </button>
            </div>

            <div className="custom-size-chart-body">
              {detail?.banners?.[0] ? (
                <img
                  src={detail.banners[0]}
                  alt="Size Chart"
                  className="custom-size-chart-image"
                />
              ) : (
                <p className="text-center mb-0">Size chart is not available.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DetailPage;
